/* eslint-disable max-lines */
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Pagination,
  Typography,
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  listExpiringSoonItemsApi,
  listInPhysicalCountItemsApi,
  listLowDaysOfSupplyItemsApi,
  listLowStockItemsApi,
} from 'api/inventory';
import { buildFacilityLabelLookup } from 'common/utils/facilityLabel';
import { selectAuthorizedFacilities } from 'store/selectors/facilitySelectors';
import { handleError } from 'store/thunks/errorHandlerThunks';

import { PAGE_SIZE, useInventoryAlert } from '../context/InventoryAlertContext';

const DIALOG_META = {
  lowStock: {
    title: 'Low Stock Items',
    subtitle: 'Items currently below their configured reorder point',
  },
  expiringSoon: {
    title: 'Expiring Soon',
    subtitle: 'Lots expiring within the next 30 days',
  },
  lowDaysOfSupply: {
    title: '14 Day Supply',
    subtitle: 'Drugs below 14-day dispensing threshold',
  },
  inPhysicalCount: {
    title: 'In Physical Count',
    subtitle: 'Items currently locked for physical counting',
  },
};

const STOCK_STATUS_SX = {
  CRITICAL_STOCK: { bgcolor: '#fdecea', color: '#c62828' },
  LOW_STOCK: { bgcolor: '#fff3e0', color: '#e65100' },
};

const StockStatusChip = ({ value }) => {
  const sx = STOCK_STATUS_SX[value] ?? {};
  const label = value === 'CRITICAL_STOCK' ? 'Critical' : 'Low';
  return <Chip label={label} size='small' sx={{ fontWeight: 600, fontSize: 11, ...sx }} />;
};
StockStatusChip.propTypes = { value: PropTypes.string.isRequired };

// Stock quantities can carry fractional precision (e.g. reorder point 9.1003).
// Round up to whole units for display so the alert grid reads cleanly.
const ceilQty = (value) => (value != null ? Math.ceil(value) : value);

const QtyWithStatus = ({ data }) => {
  if (!data) return null;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 1,
        height: '100%',
        width: '100%',
        pr: 1,
      }}
    >
      <Typography sx={{ fontSize: 13 }}>{ceilQty(data.currentQty)}</Typography>
      <StockStatusChip value={data.stockStatus} />
    </Box>
  );
};
QtyWithStatus.propTypes = {
  data: PropTypes.shape({
    currentQty: PropTypes.number,
    stockStatus: PropTypes.string,
  }).isRequired,
};

const ExpiryDaysChip = ({ value }) => {
  if (value == null) return null;
  let bgcolor;
  let color;
  if (value <= 7) {
    bgcolor = '#fdecea';
    color = '#c62828';
  } else if (value <= 14) {
    bgcolor = '#fff3e0';
    color = '#e65100';
  } else if (value <= 21) {
    bgcolor = '#fff8e1';
    color = '#f57f17';
  } else {
    bgcolor = '#e3f2fd';
    color = '#1565c0';
  }
  return (
    <Chip
      label={`${value} days`}
      size='small'
      sx={{ bgcolor, color, fontWeight: 600, fontSize: 11, height: 20 }}
    />
  );
};
ExpiryDaysChip.propTypes = { value: PropTypes.number.isRequired };

const BOLD_CELL = { fontWeight: 600 };

const daysRemainingStyle = ({ value }) => {
  if (value == null) return {};
  if (value <= 3) return { color: '#c62828', fontWeight: 600 };
  if (value <= 7) return { color: '#e65100', fontWeight: 600 };
  if (value <= 12) return { color: '#ff9800', fontWeight: 600 };
  return {};
};

const LOW_STOCK_COLS = [
  { field: 'drugName', headerName: 'Drug Name', minWidth: 200, flex: 2, cellStyle: BOLD_CELL },
  { field: 'strength', headerName: 'Strength', minWidth: 90 },
  { field: 'facilityLabel', headerName: 'Facility', minWidth: 150, flex: 1 },
  {
    field: 'currentQty',
    headerName: 'Current Qty',
    minWidth: 160,
    cellRenderer: QtyWithStatus,
  },
  {
    field: 'reorderPoint',
    headerName: 'Reorder Point',
    minWidth: 110,
    type: 'numericColumn',
    valueFormatter: ({ value }) => ceilQty(value),
  },
  {
    field: 'shortage',
    headerName: 'Shortage',
    minWidth: 100,
    type: 'numericColumn',
    cellStyle: { color: '#c62828', fontWeight: 600 },
    // API returns shortage as (currentQty - reorderPoint), i.e. negative when
    // below threshold. Flip to a positive magnitude and round up for display.
    valueFormatter: ({ value }) => (value != null ? Math.ceil(-value) : value),
  },
];

const EXPIRING_SOON_COLS = [
  { field: 'drugName', headerName: 'Drug Name', minWidth: 200, flex: 2, cellStyle: BOLD_CELL },
  { field: 'strength', headerName: 'Strength', minWidth: 90 },
  { field: 'facilityLabel', headerName: 'Facility', minWidth: 150, flex: 1 },
  { field: 'lots', headerName: 'Lots', minWidth: 90, type: 'numericColumn' },
  {
    field: 'daysUntilExpiry',
    headerName: 'Days Until Expiry',
    minWidth: 150,
    cellRenderer: ({ value }) => <ExpiryDaysChip value={value} />,
  },
];

const LOW_SUPPLY_COLS = [
  { field: 'drugName', headerName: 'Drug Name', minWidth: 200, flex: 2, cellStyle: BOLD_CELL },
  { field: 'strength', headerName: 'Strength', minWidth: 90 },
  { field: 'facilityLabel', headerName: 'Facility', minWidth: 150, flex: 1 },
  { field: 'currentQty', headerName: 'Current Qty', minWidth: 100, type: 'numericColumn' },
  {
    field: 'dailyDispensingRate',
    headerName: 'Daily Rate',
    minWidth: 130,
    type: 'numericColumn',
    valueFormatter: ({ value }) => (value != null ? `${value.toFixed(1)} units/day` : '—'),
  },
  {
    field: 'daysRemaining',
    headerName: 'Days Remaining',
    minWidth: 130,
    type: 'numericColumn',
    valueFormatter: ({ value }) => (value != null ? `${value} days` : '—'),
    cellStyle: daysRemainingStyle,
  },
];

const formatLockedAt = ({ value }) => {
  if (!value) return '—';
  const d = new Date(value);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const IN_PHYSICAL_COUNT_COLS = [
  { field: 'drugName', headerName: 'Drug Name', minWidth: 200, flex: 2, cellStyle: BOLD_CELL },
  { field: 'strength', headerName: 'Strength', minWidth: 90 },
  {
    field: 'ndc',
    headerName: 'NDC',
    minWidth: 130,
    cellStyle: { fontFamily: 'monospace', fontSize: 13 },
  },
  { field: 'facilityLabel', headerName: 'Facility', minWidth: 150, flex: 1 },
  { field: 'quantityOnHand', headerName: 'On Hand', minWidth: 100, type: 'numericColumn' },
  {
    field: 'lockedAt',
    headerName: 'Locked Since',
    minWidth: 160,
    valueFormatter: formatLockedAt,
  },
  { field: 'lockedByName', headerName: 'Locked By', minWidth: 130, flex: 1 },
];

const COL_DEFS = {
  lowStock: LOW_STOCK_COLS,
  expiringSoon: EXPIRING_SOON_COLS,
  lowDaysOfSupply: LOW_SUPPLY_COLS,
  inPhysicalCount: IN_PHYSICAL_COUNT_COLS,
};

const GRID_CONTAINER_SX = {
  position: 'relative',
  height: 500,
  '& .ag-header-cell-text': {
    textTransform: 'uppercase',
    fontSize: 11,
    color: '#888',
    fontWeight: 600,
    letterSpacing: '0.04em',
  },
};

const InventoryAlertDialog = () => {
  const reduxDispatch = useDispatch();
  const facilities = useSelector(selectAuthorizedFacilities);
  const { state, dispatch } = useInventoryAlert();
  const { alertDetails, summary } = state;
  const { active, facilityId } = alertDetails;

  const [currentPage, setCurrentPage] = useState(1);
  const gridRef = useRef(null);
  const summaryRef = useRef(summary);
  const activeRef = useRef(active);
  useEffect(() => {
    summaryRef.current = summary;
  }, [summary]);
  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  const isOpen = active !== null;
  const facilityLabelById = useMemo(() => buildFacilityLabelLookup(facilities), [facilities]);
  const detailState = active ? alertDetails[active] : null;
  /*
   * "PerfectRx" and "PerfectionRx" are two different sites, and a name on its own does not tell
   * them apart — which is how a row reads as belonging to a facility it does not. The state comes
   * from the facility record, which these rows carry only the id of.
   *
   * The page is read inside the memo: absent, it is a fresh [] on every render, which would defeat
   * the memo and hand AG Grid new row objects each time.
   */
  const rows = useMemo(
    () =>
      (detailState?.pages?.[currentPage - 1] ?? []).map((row) => ({
        ...row,
        facilityLabel: facilityLabelById[row.fulfillingPharmacyId] ?? row.facilityName ?? '',
      })),
    [detailState, currentPage, facilityLabelById]
  );
  const totalCount = detailState?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const handleClose = useCallback(() => {
    dispatch({ type: 'CLOSE_DIALOG' });
  }, [dispatch]);

  const fetchPage = useCallback(
    (dialogType, pageIndex) => {
      const { expiringSoon, lowDaysOfSupply } = summaryRef.current;
      const filter = facilityId ? { fulfillingPharmacyId: facilityId } : undefined;
      dispatch({ type: 'ROWS_LOADING', dialogType, loading: true });

      const apiFns = {
        lowStock: () =>
          listLowStockItemsApi({ filter, limit: PAGE_SIZE, offset: pageIndex * PAGE_SIZE }),
        expiringSoon: () =>
          listExpiringSoonItemsApi({
            filter,
            withinDays: expiringSoon.withinDays ?? 30,
            limit: PAGE_SIZE,
            offset: pageIndex * PAGE_SIZE,
          }),
        lowDaysOfSupply: () =>
          listLowDaysOfSupplyItemsApi({
            filter,
            thresholdDays: lowDaysOfSupply.thresholdDays ?? 14,
            limit: PAGE_SIZE,
            offset: pageIndex * PAGE_SIZE,
          }),
        inPhysicalCount: () =>
          listInPhysicalCountItemsApi({ filter, limit: PAGE_SIZE, offset: pageIndex * PAGE_SIZE }),
      };

      apiFns[dialogType]()
        .then((result) => {
          if (activeRef.current !== dialogType) return;
          dispatch({
            type: 'ROWS_LOADED',
            dialogType,
            page: pageIndex,
            rows: result.items,
            totalCount: result.totalCount,
          });
        })
        .catch((err) => {
          if (activeRef.current !== dialogType) return;
          dispatch({ type: 'PAGE_LOAD_FAILED', dialogType, page: pageIndex });
          reduxDispatch(handleError(err));
        });
    },
    [dispatch, reduxDispatch, facilityId]
  );

  // Reset to page 1 when dialog type changes
  useEffect(() => {
    setCurrentPage(1);
  }, [active]);

  // Fetch page if not cached and not already loading
  const cachedPage = alertDetails[active]?.pages?.[currentPage - 1];
  const isLoadingPage = alertDetails[active]?.rowsLoading;
  useEffect(() => {
    if (!active) return;
    if (!cachedPage && !isLoadingPage) fetchPage(active, currentPage - 1);
  }, [active, currentPage, cachedPage, isLoadingPage, fetchPage]);

  const columnDefs = useMemo(() => (active ? COL_DEFS[active] : []), [active]);
  const meta = active ? DIALOG_META[active] : null;

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth='lg' fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          pb: 2,
          px: 2.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box>
          <Typography variant='h6' sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {meta?.title}
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary', mt: 0.5 }}>
            {meta?.subtitle}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size='small' sx={{ mt: 0.25 }}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ px: 2, display: 'flex', flexDirection: 'column' }}>
          {/* reserved row for future filters */}
          <Box sx={{ minHeight: 32 }} />
          <Box sx={GRID_CONTAINER_SX} className='ag-theme-alpine'>
            {detailState?.rowsLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(255,255,255,0.75)',
                  zIndex: 10,
                }}
              >
                <CircularProgress size={36} />
              </Box>
            )}
            <AgGridReact
              ref={gridRef}
              rowData={rows}
              columnDefs={columnDefs}
              defaultColDef={{ resizable: true, sortable: true, suppressMovable: true }}
              suppressPaginationPanel
            />
          </Box>
        </Box>

        {totalPages > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              px: 2,
              py: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              size='small'
              color='primary'
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button
          variant='contained'
          onClick={handleClose}
          sx={{ bgcolor: '#0d1f35', '&:hover': { bgcolor: '#1a3a5c' }, px: 3 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InventoryAlertDialog;
